import React, { useEffect, useRef } from 'react';
import { View, Text, Animated, Platform, Easing } from 'react-native';
import Ionicons from '@expo/vector-icons/Ionicons';
import { brandToastConfirmTokens } from './brandToastTokens';

interface Props {
  /**
   * Visibility flag controlled by the parent. Toast slides in when this
   * flips to true, auto-dismisses after `durationMs`, and slides out.
   */
  visible: boolean;
  /** Auto-dismiss callback fired once the slide-out animation completes. */
  onHide: () => void;
  /** Toast body. Single short line — keep under ~50 chars. */
  message: string;
  /**
   * Auto-dismiss delay. Default 2.6s reads as «noticed without being
   * intrusive». Set to 0 to keep it open until parent flips visible=false.
   */
  durationMs?: number;
  /**
   * Lead icon. Defaults to a green checkmark — use for confirmations.
   * Pass a different name (e.g. `info`) for non-confirmation contexts.
   */
  iconName?: React.ComponentProps<typeof Ionicons>['name'];
  iconColor?: string;
}

/**
 * Brand-aligned confirmation toast — slides down from the top with the
 * Agapella card material, holds for ~2.6 s, slides back up. Replaces the
 * jarring `window.alert?.()` Safari modals on web. Non-blocking.
 *
 * Owner directive 2026-05-08: «Добавлено в календарь» feedback should
 * read as if Agapella herself sent a soft confirmation, not a system
 * dialog — a slide-down card with the brand mark is the closest thing
 * we have without building a full toast manager today.
 */
export const BrandToast: React.FC<Props> = ({
  visible,
  onHide,
  message,
  durationMs = 2600,
  iconName = 'checkmark',
  iconColor = brandToastConfirmTokens.iconColor,
}) => {
  const translateY = useRef(new Animated.Value(-80)).current;
  const opacity = useRef(new Animated.Value(0)).current;
  const hideTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    if (!visible) return;

    Animated.parallel([
      Animated.timing(translateY, {
        toValue: 0,
        duration: 280,
        easing: Easing.out(Easing.cubic),
        useNativeDriver: true,
      }),
      Animated.timing(opacity, {
        toValue: 1,
        duration: 240,
        useNativeDriver: true,
      }),
    ]).start();

    if (durationMs > 0) {
      hideTimer.current = setTimeout(() => {
        Animated.parallel([
          Animated.timing(translateY, {
            toValue: -80,
            duration: 220,
            easing: Easing.in(Easing.cubic),
            useNativeDriver: true,
          }),
          Animated.timing(opacity, {
            toValue: 0,
            duration: 200,
            useNativeDriver: true,
          }),
        ]).start(() => onHide());
      }, durationMs);
    }

    return () => {
      if (hideTimer.current) clearTimeout(hideTimer.current);
    };
    // We deliberately depend only on `visible` so the show animation
    // fires once per visibility flip, not on every parent re-render.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [visible]);

  if (!visible) return null;

  return (
    <Animated.View
      pointerEvents="none"
      style={{
        position: Platform.OS === 'web' ? ('fixed' as any) : 'absolute',
        top: 16,
        left: 0,
        right: 0,
        alignItems: 'center',
        zIndex: 9999,
        transform: [{ translateY }],
        opacity,
      } as any}
    >
      <View
        style={{
          flexDirection: 'row',
          alignItems: 'center',
          gap: 12,
          paddingHorizontal: 16,
          paddingVertical: 11,
          borderRadius: brandToastConfirmTokens.borderRadius,
          backgroundColor: brandToastConfirmTokens.background,
          borderWidth: 1,
          borderColor: brandToastConfirmTokens.borderColor,
          ...(Platform.OS === 'web'
            ? ({ boxShadow: brandToastConfirmTokens.shadow.web } as any)
            : {
                shadowColor: brandToastConfirmTokens.shadow.native.color,
                shadowOffset: brandToastConfirmTokens.shadow.native.offset,
                shadowOpacity: brandToastConfirmTokens.shadow.native.opacity,
                shadowRadius: brandToastConfirmTokens.shadow.native.radius,
                elevation: brandToastConfirmTokens.shadow.native.elevation,
              }),
          maxWidth: 480,
        } as any}
      >
        <View
          style={{
            width: brandToastConfirmTokens.iconHaloSize,
            height: brandToastConfirmTokens.iconHaloSize,
            borderRadius: brandToastConfirmTokens.iconHaloSize / 2,
            backgroundColor: brandToastConfirmTokens.iconHaloBackground,
            alignItems: 'center',
            justifyContent: 'center',
          } as any}
        >
          <Ionicons name={iconName} size={brandToastConfirmTokens.iconSize} color={iconColor} />
        </View>
        <Text
          style={{
            fontSize: 13,
            fontWeight: '500',
            color: brandToastConfirmTokens.textColor,
            letterSpacing: -0.1,
            flexShrink: 1,
          }}
        >
          {message}
        </Text>
      </View>
    </Animated.View>
  );
};

export default BrandToast;
